<template>
  <el-form
    ref="form"
    :model="formValue"
    :rules="formRules"
    @status="handleFormStatusChange"
  >
    <div
      v-if="isContactCreationTypeSelectorShown"
      :class="contactCreationTypeSelectorClass"
    >
      <div>
        <el-radio
          :value="selectedContactCreationType"
          label="existing"
          :disabled="isDisabled"
          data-test-id="contact-form.radio-existing"
          @change="handleSelectContactCreationTypeChange"
        >
          {{ $t('profile.contact-list.existing-contact') }}
        </el-radio>
      </div>
      <div>
        <el-radio
          :value="selectedContactCreationType"
          label="new"
          :disabled="isDisabled"
          data-test-id="contact-form.radio-new"
          @change="handleSelectContactCreationTypeChange"
        >
          {{ $t('profile.contact-list.new-contact') }}
        </el-radio>
      </div>
    </div>
    <div
      v-if="isContactSelectorShown"
      :class="contactSelectorClass"
    >
      <el-select
        :value="selectedContactId"
        :label="$t('profile.contact-list.contact')"
        filterable
        :disabled="isDisabled"
        data-test-id="contact-form.select-contact"
        @change="handleSelectedContactIdChange"
      >
        <el-option
          v-for="contact in contactList"
          :key="contact.id"
          :label="getContactLabel(contact)"
          :value="contact.id"
          :data-test-id="`contact-form.select-contact.item-${contact.id}`"
        />
      </el-select>
    </div>
    <template v-if="isContactFormShown">
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="firstName">
            <el-input
              v-model="formValue.firstName"
              :label="$t('profile.contact-list.first-name')"
              :disabled="isSelectedContactCreationTypeExisting"
              :required="!isSelectedContactCreationTypeExisting"
              data-test-id="contact-form.input-first-name"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="lastName">
            <el-input
              v-model="formValue.lastName"
              :label="$t('profile.contact-list.last-name')"
              :disabled="isSelectedContactCreationTypeExisting"
              :required="!isSelectedContactCreationTypeExisting"
              data-test-id="contact-form.input-last-name"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="email">
            <el-input
              v-model="formValue.email"
              :label="$t('profile.contact-list.email')"
              :disabled="isSelectedContactCreationTypeExisting"
              :required="!isSelectedContactCreationTypeExisting"
              data-test-id="contact-form.input-email"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="phone">
            <el-input
              v-model="formValue.phone"
              :label="$t('profile.contact-list.phone')"
              prefix-value="+"
              :disabled="isSelectedContactCreationTypeExisting"
              data-test-id="contact-form.input-phone"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="jobTitle">
            <el-input
              v-model="formValue.jobTitle"
              :label="$t('profile.contact-list.job-title')"
              :disabled="isSelectedContactCreationTypeExisting"
              data-test-id="contact-form.input-job-title"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </template>
    <div
      v-if="isContactTypeSelectorShown"
      class="mb-16"
    >
      <el-form-item
        prop="type"
        :inline-message="false"
      >
        <div>
          <hint
            class="mb-16"
            :label="$t('profile.contact-list.type')"
          >
            <template #content>
              <div>
                {{ $t('profile.contact-list.type.tooltip.heading') }}
              </div>
              <br>
              <div>
                {{ $t('profile.contact-list.type.tooltip.block-billing') }}
              </div>
              <br>
              <div>
                {{ $t('profile.contact-list.type.tooltip.block-business') }}
              </div>
              <br>
              <div>
                {{ $t('profile.contact-list.type.tooltip.block-technical') }}
              </div>
            </template>
          </hint>
        </div>
        <el-checkbox-group
          v-model="formValue.type"
          data-test-id="contact-form.checkbox-group-contact-type"
        >
          <el-checkbox
            v-for="contactType in contactTypes"
            :key="contactType.key"
            :label="contactType.value"
            class="mr-16"
            :data-test-id="`contact-form.checkbox-group-contact-type.checkbox-${contactType.key}`"
          >
            {{ $t(`profile.contact-list.type.${contactType.key}`) }}
          </el-checkbox>
        </el-checkbox-group>
      </el-form-item>
      <div
        v-if="showConsentText"
        class="my-24"
      >
        {{ $t('profile.contact-list.consent-message') }}
      </div>
    </div>
    <slot />
  </el-form>
</template>

<script>
import { ContactType } from '@/app/domain/Contact';
import { isEmailValid } from '@/app/library/isEmailValid';
import { isEmpty } from '@/app/library/isEmpty';
import Hint from '@/app/components/Hint';

export default {
  name: 'ContactForm',
  components: {
    Hint
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    contactList: {
      type: Array,
      default: () => []
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit'].includes(value)
    },
    isExtended: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    showConsentText: {
      type: Boolean,
      default: true
    }
  },
  emits: [
    'input',
    'status'
  ],
  data() {
    return {
      formValue: this.convertFormValueToProxy(this.getDefaultFormValue()),
      formRules: {
        type: [
          {
            required: true,
            message: this.$t('form-error.required', { field: this.$t('profile.contact-list.type') })
          }
        ],
        firstName: [
          {
            required: true,
            message: this.$t('form-error.required', { field: this.$t('profile.contact-list.first-name') })
          }
        ],
        lastName: [
          {
            required: true,
            message: this.$t('form-error.required', { field: this.$t('profile.contact-list.last-name') })
          }
        ],
        email: [
          {
            validator: this.validateEmail
          }
        ],
        phone: [
          {
            pattern: /^[0-9+\-#\s()]+$/,
            message: this.$t('form-error.phone.incorrect')
          }
        ]
      },
      isContactCreation: true,
      selectedContactCreationType: 'existing',
      selectedContactId: null,
      contactTypes: this.getContactTypes()
    };
  },
  computed: {
    isContactCreationTypeSelectorShown() {
      return this.isContactCreation;
    },
    isContactSelectorShown() {
      return this.isContactCreation && this.isSelectedContactCreationTypeExisting;
    },
    isContactTypeSelectorShown() {
      return (this.isSelectedContactCreationTypeNew || !this.isFormValueEmpty) && this.isExtended;
    },
    isContactFormShown() {
      return this.isSelectedContactCreationTypeNew || !this.isFormValueEmpty;
    },
    isSelectedContactCreationTypeExisting() {
      return this.isContactCreation && this.selectedContactCreationType === 'existing';
    },
    isSelectedContactCreationTypeNew() {
      return this.isContactCreation && this.selectedContactCreationType === 'new';
    },
    isFormValueEmpty() {
      return Object.keys(this.formValue).every((key) => isEmpty(this.formValue[key]));
    },
    contactCreationTypeSelectorClass() {
      return !this.isContactSelectorShown && this.isContactTypeSelectorShown
        ? 'mb-8'
        : 'mb-16';
    },
    contactSelectorClass() {
      return this.isContactTypeSelectorShown
        ? 'mb-16'
        : 'mb-24';
    }
  },
  watch: {
    isDisabled(value) {
      if (value) {
        this.resetFormValue();
        this.resetState();
      }
    }
  },
  created() {
    this.setFormValue(this.value);
    this.setFormMode(this.mode);
  },
  async mounted() {
    await this.$nextTick();
    this.$refs.form.clearValidate();
  },
  methods: {
    handleFormStatusChange(status) {
      this.$emit('status', status);
    },
    handleSelectContactCreationTypeChange(type) {
      this.selectedContactCreationType = type;
      if (this.selectedContactId && type === 'existing') {
        this.selectContact(this.selectedContactId);
      } else {
        this.resetFormValue();
      }
    },
    handleSelectedContactIdChange(id) {
      if (id) {
        this.selectContact(id);
      }
    },
    convertFormValueToProxy(formValue) {
      const self = this;

      return new Proxy(formValue, {
        get(target, prop) {
          return target[prop];
        },
        set(target, prop, value) {
          target[prop] = value;
          self.$emit('input', target);

          return true;
        }
      });
    },
    getDefaultFormValue() {
      return {
        id: undefined,
        type: [],
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        phone: '',
        jobTitle: undefined,
        userId: undefined
      };
    },
    setFormValue(value) {
      Object.keys(value).forEach((key) => {
        this.formValue[key] = value[key];
      });
    },
    resetFormValue() {
      this.setFormValue(this.getDefaultFormValue());
      if (this.$refs.form) {
        this.$refs.form.resetInitialValue();
        this.$refs.form.resetFields();
      }
    },
    setFormMode(mode) {
      this.isContactCreation = mode === 'create';

      if (this.isContactCreation) {
        if (!this.isFormValueEmpty) {
          if (this.value.id) {
            this.selectedContactCreationType = 'existing';
            this.selectContact(this.value.id);
          } else {
            this.selectedContactCreationType = 'new';
          }
        }
      }
    },
    resetState() {
      this.isContactCreation = true;
      this.selectedContactCreationType = 'existing';
      this.selectedContactId = null;
    },
    selectContact(id) {
      this.selectedContactId = id;
      this.resetFormValue();
      this.setFormValue(this.getContactById(id));
    },
    getContactById(id) {
      return this.contactList.find((contact) => contact.id === id);
    },
    getContactLabel(contact) {
      return [contact.firstName, contact.lastName].join(' ');
    },
    getContactTypes() {
      return [
        ContactType.Billing,
        ContactType.Technical,
        ContactType.Business
      ].map((contactTypeValue) => ({
        key: ContactType[contactTypeValue],
        value: contactTypeValue
      }));
    },
    validateEmail(_, value, callback) {
      if (!value) {
        callback(new Error(this.$t('form-error.required', { field: this.$t('profile.contact-list.email') })));
      } else if (!isEmailValid(value)) {
        callback(new Error(this.$t('form-error.email.incorrect')));
      } else {
        callback();
      }
    },
    async validate() {
      try {
        return await this.$refs.form.validate();
      } catch (e) {
        return false;
      }
    }
  }
};
</script>
